import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { SnackBarService } from '../../../services/snackbar.service';

/**
 * Renders the SnackBarService queue: bottom-center pills, kind-colored accent,
 * click (or ×) to dismiss early. Lives once in the app shell, outside the
 * signed-in/out branches so feedback works everywhere. Positioned apart from
 * the notification toasts (bottom-right) so the two never collide.
 */
@Component({
  selector: 'app-snackbar-host',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (snackbar.snacks().length) {
      <div class="sbh">
        @for (s of snackbar.snacks(); track s.id) {
          <div
            class="sbh__snack"
            [class.sbh__snack--success]="s.kind === 'success'"
            [class.sbh__snack--error]="s.kind === 'error'"
            [attr.role]="s.kind === 'error' ? 'alert' : 'status'"
            (click)="snackbar.dismiss(s.id)"
          >
            <span class="sbh__ico" aria-hidden="true">
              @if (s.kind === 'success') {
                <i class="pi pi-check-circle"></i>
              } @else if (s.kind === 'error') {
                <i class="pi pi-exclamation-circle"></i>
              } @else {
                <i class="pi pi-info-circle"></i>
              }
            </span>
            <span class="sbh__msg">{{ s.message }}</span>
            <span class="sbh__close" aria-hidden="true">×</span>
          </div>
        }
      </div>
    }
  `,
  styles: [
    `
      .sbh {
        position: fixed;
        left: 50%;
        bottom: 20px;
        transform: translateX(-50%);
        z-index: var(--ap-z-toast);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        max-width: min(480px, calc(100vw - 32px));
        width: max-content;
      }
      .sbh__snack {
        display: flex;
        align-items: center;
        gap: 10px;
        max-width: 100%;
        padding: 11px 16px;
        border-radius: 980px;
        background: #1d1d1f;
        color: #fff;
        font-size: 14px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
        cursor: pointer;
        animation: sbh-in 0.22s ease;
      }
      @keyframes sbh-in {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
        to {
          opacity: 1;
          transform: none;
        }
      }
      .sbh__ico {
        flex-shrink: 0;
        display: grid;
        place-items: center;
        font-size: 16px;
      }
      .sbh__snack--success .sbh__ico {
        color: #4ade80;
      }
      .sbh__snack--error .sbh__ico {
        color: #f87171;
      }
      .sbh__msg {
        min-width: 0;
      }
      .sbh__close {
        margin-left: 4px;
        color: rgba(255, 255, 255, 0.55);
        font-size: 15px;
        line-height: 1;
      }
      @media (prefers-reduced-motion: reduce) {
        .sbh__snack {
          animation: none;
        }
      }
    `,
  ],
})
export class SnackbarHostComponent {
  readonly snackbar = inject(SnackBarService);
}
